'use client';

import { Button } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';
import { Bookmark } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { LucideIcon } from '@/src/components/LucideIcon';
import { INERT_SAVE_SEARCH_CONTEXT, useOptionalSrpSaveSearch } from './SrpSaveSearchProvider';

/**
 * `testIdSuffix` is required, and deliberately so. The `(search)` layout keeps
 * the header and sticky bars mounted at the same time and switches them with
 * CSS alone (a third, floating bar joins them in mobile map mode), so a bare
 * `button-save-search` always matches two nodes and resolves to a hidden one at
 * some viewport. There is no correct bare selector to hand out, so every caller
 * names its surface - the rule `HomepageListingSearches` already follows, and
 * that `HomePage.test.tsx` asserts by checking the unsuffixed id is absent.
 */
export function SrpSaveSearchButton({ testIdSuffix }: { testIdSuffix: string }) {
  const saveSearch = useOptionalSrpSaveSearch();
  const hasLoggedMissingContext = useRef(false);

  useEffect(() => {
    if (!saveSearch && !hasLoggedMissingContext.current) {
      hasLoggedMissingContext.current = true;
      logError({
        error: new Error('SrpSaveSearchButton rendered without SrpSaveSearchProvider'),
        errorType: 'MissingSrpSaveSearchProvider',
      });
    }
  }, [saveSearch]);

  const { isSaved, isDisabled, label, accessibleLabel, opensDialog, handleToggle } =
    saveSearch ?? INERT_SAVE_SEARCH_CONTEXT;
  const handleClick = () => {
    if (!isDisabled) {
      handleToggle();
    }
  };

  const icon = <LucideIcon icon={Bookmark} size="sm" fill={isSaved ? 'currentColor' : 'none'} />;

  return (
    <Button
      data-testid={`button-save-search-${testIdSuffix}`}
      type="button"
      tone="brand"
      emphasis="filled"
      size="sm"
      icon={icon}
      iconPosition="start"
      css={{
        whiteSpace: 'nowrap',
        borderRadius: 'pill',
      }}
      onClick={handleClick}
      disabled={isDisabled}
      // A toggle only on the one path that flips state in place; everywhere
      // else the click opens a dialog, and `aria-pressed` would promise a
      // state change that never happens.
      aria-pressed={opensDialog ? undefined : isSaved}
      aria-haspopup={opensDialog ? 'dialog' : undefined}
      aria-label={accessibleLabel}
    >
      {label}
    </Button>
  );
}
